<template>
  <div>
    <div
      class="modal fade"
      :class="{ 'show d-block': enrichmentVisibility }"
      id="enrichmentModal"
      tabindex="-1"
      aria-labelledby="enrichmentTitle"
      aria-modal="true"
      role="dialog"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <form @submit.prevent="submit" class="modal-content" autocomplete="off">
          <div class="modal-header d-flex flex-column align-items-center py-3 px-4">
            <div class="text-center text-accent mb-0" id="enrichmentTitle">
              {{ parameters.enrichmentTitle || $t('enrichment.title') }}
            </div>
          </div>

          <div class="modal-body px-4 pb-0" style="overflow-y: auto;">
            <div class="form-group" v-if="parameters.showNameEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">
                {{ $t('enrichment.labelName') }}
              </small>
              <input
                id="enrichmentName"
                :aria-label="$t('enrichment.labelName')"
                v-model="name"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                :placeholder="$tc('enrichment.PlaceholderName')"
                ref="enrichmentName"
              />
            </div>

            <div class="form-group" v-if="parameters.showEmailEnrichment">
              <small
                class="text-accent d-block"
                :class="{ 'text-dark': whiteMode, 'text-danger': emailState === 'invalid' }"
                >{{ $t('enrichment.labelEmail') }}
              </small>
              <input
                v-model="email"
                type="email"
                id="enrichmentEmail"
                :aria-label="$t('enrichment.labelEmail')"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                placeholder="email@email.com"
                :class="{ 'text-danger is-invalid': emailState === 'invalid' }"
              />
              <small
                v-if="emailState === 'invalid'"
                class="form-text text-danger mt-1 d-flex align-items-center fade"
                :class="{ show: emailState === 'invalid' }"
              >
                <img v-show="emailState === 'invalid'" class="mr-2" src="/images/close.svg" />
                {{ errorMsgEmail }}</small
              >
            </div>

            <div class="form-group" v-if="parameters.showGenderEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelGender')
              }}</small>
              <select
                id="enrichmentGender"
                :aria-label="$t('enrichment.labelGender')"
                v-model="gender"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                ref="enrichmentGender"
              >
                <option value="m">{{ $t('enrichment.maleGender') }}</option>
                <option value="f">{{ $t('enrichment.femaleGender') }}</option>
                <option value="o">{{ $t('enrichment.otherGender') }}</option>
              </select>
            </div>

            <div id="enrichmentDocumentFormGroup" class="form-group" v-if="showDocumentInput">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t(labelDocumentInput)
              }}</small>
              <the-mask
                id="enrichmentDocument"
                :aria-label="$t(labelDocumentInput)"
                type="tel"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                ref="document"
                :placeholder="maskPlaceholderInput"
                :mask="maskDocumentInput"
                v-model="document"
                :class="{ 'text-danger': errorMsgDocument !== '' }"
                @input="errorMsgDocument = ''"
              ></the-mask>
              <small
                v-if="errorMsgDocument"
                class="form-text text-danger mt-2 d-flex align-items-center fade"
                :class="{ show: errorMsgDocument !== '' }"
              >
                <img v-show="errorMsgDocument !== ''" class="mr-2" src="/images/close.svg" />
                {{ errorMsgDocument }}</small
              >
            </div>

            <div v-if="parameters.showAddressEnrichment">
              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.zipcode')
                }}</small>
                <input
                  id="enrichmentAddressZipCode"
                  :aria-label="$t('enrichment.zipcode')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  placeholder="00000-000"
                  v-mask="'#####-###'"
                  v-model="zipcode"
                />
                <small
                  v-if="errorMsgZipCode"
                  class="form-text text-danger mt-2 d-flex align-items-center fade"
                  :class="{ show: errorMsgZipCode !== '' }"
                >
                  <img v-show="errorMsgZipCode !== ''" class="mr-2" src="/images/close.svg" />
                  {{ errorMsgZipCode }}</small
                >
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.state')
                }}</small>
                <input
                  id="enrichmentAddressState"
                  :aria-label="$t('enrichment.state')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.statePlaceholder')"
                  v-model="state"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.city')
                }}</small>
                <input
                  id="enrichmentAddressCity"
                  :aria-label="$t('enrichment.city')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.cityPlaceholder')"
                  v-model="city"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.neighbor')
                }}</small>
                <input
                  id="enrichmentAddressNeighbor"
                  :aria-label="$t('enrichment.neighbor')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.neighborPlaceholder')"
                  v-model="neighbor"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.address')
                }}</small>
                <input
                  id="enrichmentAddress"
                  :aria-label="$t('enrichment.address')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  :placeholder="$tc('enrichment.addressPlaceholder')"
                  v-model="address"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.number')
                }}</small>
                <input
                  id="enrichmentAddressNumber"
                  :aria-label="$t('enrichment.number')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  placeholder="00"
                  v-model="number"
                  ref="inputNumber"
                />
              </div>

              <div class="form-group">
                <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                  $t('enrichment.complement')
                }}</small>
                <input
                  id="enrichmentAddressComplement"
                  :aria-label="$t('enrichment.complement')"
                  class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                  v-model="complement"
                />
              </div>
            </div>

            <div class="form-group" v-if="parameters.showPhoneEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">{{
                $t('enrichment.labelPhone')
              }}</small>

              <InternationalPhone v-model="phone" :accessibleLabel="$t('enrichment.labelPhone')" />

              <small
                v-if="phoneState === 'invalid'"
                class="form-text text-danger mt-1 d-flex align-items-center fade"
                :class="{ show: phoneState === 'invalid' }"
              >
                <img v-show="phoneState === 'invalid'" class="mr-2" src="/images/close.svg" />
                {{ errorMsgPhone }}</small
              >
            </div>

            <div class="form-group" v-if="parameters.showBirthEnrichment">
              <small class="text-accent d-block" :class="{ 'text-dark': whiteMode }">Data de Nascimento</small>
              <input
                v-mask="'##/##/####'"
                placeholder="00/00/0000"
                v-model="birth"
                type="tel"
                id="enrichmentBirth"
                :aria-label="$t('enrichment.labelBirth')"
                class="text-success border-top-0 border-left-0 border-right-0 form-control px-2"
                :class="{ 'text-danger is-invalid': birthState === 'invalid' }"
              />
              <small
                v-if="birthState === 'invalid'"
                class="form-text text-danger mt-1 d-flex align-items-center fade"
                :class="{ show: birthState === 'invalid' }"
              >
                <img v-show="birthState === 'invalid'" class="mr-2" src="/images/close.svg" />
                {{ errorMsgBirth }}</small
              >
            </div>

            <div v-for="(a, index) in parameters.enrichAgreements" :key="index" class="form-group form-check">
              <input
                class="form-check-input cursor-pointer"
                type="checkbox"
                v-model="terms"
                :required="verifyFieldHasData && a.required"
                :value="{ name: a.name, required: a.required }"
                :id="`check${index}`"
              />
              <label class="form-check-label cursor-pointer fs-12" :for="`check${index}`">
                <a target="_blank" :href="a.href" v-if="a.href">{{ a.label }}</a>
                <span v-else>{{ a.label }}</span>
              </label>
            </div>
            <div v-if="parameters.enrichDisclaimer" class="form-group form-check">
              <label class="form-check-label text-muted fs-12" v-html="parameters.enrichDisclaimer"></label>
            </div>
          </div>

          <div class="modal-footer flex-column text-center border-0 p-0 pt-2 pb-4 mb-4">
            <div>
              <button
                id="submitButton"
                type="submit"
                class="btn btn-accent bg-accent btn-round shadow btn-lg"
                :class="{ 'bg-dark': whiteMode }"
                style="min-width: 132px;"
              >
                {{ !parameters.preEnrich ? $t('enrichment.finish') : $t('enrichment.start') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div
      class="modal-backdrop fade show"
      id="backdrop"
      style="display: none;"
      :class="{ 'd-block': enrichmentVisibility }"
    ></div>
  </div>
</template>
<script>
import { validate as validateCPF } from 'gerador-validador-cpf';
import { TheMask, mask } from 'vue-the-mask';
import PrivacyLinks from '@/components/PrivacyLinks.vue';
import { isCNPJ } from 'validation-br';
import AddressService from '../services/AddressService';
import InternationalPhone from './InternationalPhone.vue';

export default {
  components: {
    PrivacyLinks,
    TheMask,
    InternationalPhone,
  },
  props: { parameters: { type: Object, default: {} } },
  directives: {
    mask,
  },
  data() {
    return {
      name: '',
      email: '',
      gender: '',
      zipcode: '',
      state: '',
      city: '',
      neighbor: '',
      address: '',
      number: '',
      complement: '',
      phone: '',
      document: '',
      birth: '',
      emailState: 'blank',
      documentState: 'blank',
      phoneState: 'blank',
      birthState: 'blank',
      errorMsgEmail: '',
      errorMsgDocument: '',
      errorMsgZipCode: '',
      errorMsgPhone: '',
      errorMsgBirth: '',
      terms: [],
      resolve: () => null,
      enrichmentVisibility: false,
    };
  },
  watch: {
    enrichmentVisibility(val) {
      const vm = this;
      if (val) {
        vm.$nextTick(() => vm.$el.querySelector('.modal-body input, .modal-body select')?.focus());
      } else {
        vm.resolve(null);
      }
    },
    zipcode(value) {
      if (this.isZipcodeValid(value)) {
        AddressService.getAddressByCEP(value).then((address) => {
          if (!address.erro) {
            this.fillAddressInputs(address);
          }
        });
      }
    },
  },
  computed: {
    isTermsChecked() {
      return this.terms.filter((s) => s.required).length ===
        this.parameters.enrichAgreements.filter((s) => s.required).length
        ? true
        : false;
    },
    whiteMode() {
      if (this.parameters) {
        return this.parameters.color === '#ffffff';
      } else {
        return false;
      }
    },
    showDocumentInput() {
      return this.parameters.showDocumentEnrichment || this.parameters.showEntityDocumentEnrichment;
    },
    maskDocumentInput() {
      const maskDocumentInput = [];
      if (this.parameters.showDocumentEnrichment) maskDocumentInput.push('###.###.###-##');
      if (this.parameters.showEntityDocumentEnrichment) maskDocumentInput.push('XX.XXX.XXX/XXXX-##');
      return maskDocumentInput;
    },
    maskPlaceholderInput() {
      const placeholder = this.maskDocumentInput;
      if (placeholder.length > 1) {
        return '000.000.000-00 (/0000-00)';
      }
      return placeholder[0].replace(/#/g, '0');
    },
    labelDocumentInput() {
      if (this.parameters.showDocumentEnrichment && this.parameters.showEntityDocumentEnrichment) {
        return 'enrichment.labelDocumentOrEntityDocument';
      } else if (this.parameters.showDocumentEnrichment) {
        return 'enrichment.labelDocument';
      }
      return 'enrichment.labelEntityDocument';
    },
    verifyFieldHasData() {
      return this.email !== '' || this.phone !== '' || this.document !== '' || this.birth !== '';
    },
  },
  mounted() {
    if (!this.parameters.showEmailEnrichment) {
      this.emailState = 'valid';
    }
    if (!this.parameters.showPhoneEnrichment) {
      this.phoneState = 'valid';
    }
    if (!this.parameters.showDocumentEnrichment) {
      this.documentState = 'valid';
    }
    if (!this.parameters.showBirthEnrichment) {
      this.birthState = 'valid';
    }
  },
  methods: {
    isZipcodeValid(zipcode) {
      return /^[0-9]{5}-[0-9]{3}$/.test(zipcode);
    },
    openModal() {
      this.enrichmentVisibility = true;
    },
    closeModal() {
      this.enrichmentVisibility = false;
    },
    clearFields() {
      this.name = '';
      this.email = '';
      this.gender = '';
      this.zipcode = '';
      this.state = '';
      this.city = '';
      this.neighbor = '';
      this.address = '';
      this.number = '';
      this.complement = '';
      this.birth = '';
      this.phone = '';
      this.document = '';
      this.birth = '';
      this.errorMsgEmail = '';
      this.errorMsgDocument = '';
      this.errorMsgPhone = '';
      this.errorMsgBirth = '';
    },

    validateEmail(email) {
      const regexEmail = /\S+@\S+\.\S+/;
      if (regexEmail.test(email) || email === '') {
        this.emailState = 'valid';
        this.errorMsgEmail = '';
      } else {
        this.emailState = 'invalid';
        this.errorMsgEmail = 'E-mail inválido';
      }
    },

    validatePhone(phone) {
      if (phone.length === 15 || phone === '') {
        this.phoneState = 'valid';
        this.errorMsgPhone = '';
      } else {
        this.phoneState = 'invalid';
        this.errorMsgPhone = 'Telefone inválido';
      }
    },

    validateBirth(birth) {
      const [day, month, year] = birth.split('/').map(Number);
      const birthdayDate = new Date(year, month, day);
      const hasBorn = new Date(year, month - 1, day);
      const minimumBirthYear = new Date(1910, 0, 1);

      const actualDate = new Date();
      const isBigger = hasBorn.getTime() <= actualDate.getTime();
      const isLess = birthdayDate.getTime() >= minimumBirthYear.getTime();

      if (birth === '') {
        this.birthState = 'valid';
        this.errorMsgBirth = '';
      } else if (!isBigger || !isLess) {
        this.birthState = 'invalid';
        this.errorMsgBirth = this.$t('enrichment.invalidDate');
      } else {
        this.birthState = 'valid';
        this.errorMsgBirth = '';
      }
    },

    getBirthDate(birth) {
      const [day, month, year] = birth.split('/').map(Number);
      return new Date(year, month - 1, day);
    },

    askForEnrichmentParameter() {
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.openModal();
      });
    },

    fillAddressInputs(address) {
      this.state = address.uf;
      this.city = address.localidade;
      this.neighbor = address.bairro;
      this.address = address.logradouro;
      this.$refs.inputNumber.focus();
    },

    submit() {
      const inputEmail = document.getElementById('enrichmentEmail');
      const inputPhone = document.getElementById('enrichmentPhone');
      const inputDocument = document.getElementById('enrichmentDocument');
      const inputBirth = document.getElementById('enrichmentBirth');

      const enrichmentParameters = {};

      if (this.parameters.showNameEnrichment) {
        enrichmentParameters.name = this.name;
      }

      if (this.parameters.showEmailEnrichment) {
        this.validateEmail(this.email);
        if (this.emailState === 'valid') {
          enrichmentParameters.email = this.email;
          this.errorMsgEmail = '';
        } else {
          this.errorMsgEmail = 'E-mail inválido';
        }
      }

      if (this.parameters.showGenderEnrichment) {
        enrichmentParameters.gender = this.gender;
      }

      if (this.parameters.showAddressEnrichment) {
        enrichmentParameters.address = {
          zipcode: this.zipcode.replace('-', ''),
          address: this.address,
          number: this.number,
          complement: this.complement,
          neighbor: this.neighbor,
          city: this.city,
          state: this.state,
        };
      }

      if (this.parameters.showPhoneEnrichment && inputPhone) {
        if (inputPhone.checkValidity()) {
          enrichmentParameters.phone = this.phone;
          this.errorMsgPhone = '';
        } else {
          this.errorMsgPhone = this.$t('enrichment.requiredField');
        }
      }

      if (this.parameters.showEntityDocumentEnrichment || this.parameters.showDocumentEnrichment) {
        enrichmentParameters.cpf = this.document;
        this.errorMsgDocument = '';
        if (this.document.length > 0) {
          const isCPFValid = this.validateDocument();
          const isCNPJValid = this.validateEntityDocument();

          if (!isCPFValid && !isCNPJValid) {
            enrichmentParameters.cpf = '';
            this.errorMsgDocument = this.$t('enrichment.invalidDocument');
            this.$refs.document.$el.focus();
            return;
          }
        }
      }

      if (this.parameters.showBirthEnrichment) {
        this.validateBirth(this.birth);
        if (this.birthState === 'valid') {
          enrichmentParameters.birth = this.getBirthDate(this.birth);
          this.errorMsgBirth = '';
        } else {
          this.errorMsgBirth = 'Data inválida';
        }
      }

      if (this.parameters.enrichAgreements) {
        const agreements = this.parameters.enrichAgreements.map((s) => ({
          name: s.name,
          version: s.version,
          text: s.text,
          href: s.href,
        }));

        for (const iterator of agreements) {
          iterator.accept = false;
          for (const t of this.terms) {
            if (iterator.name === t.name) {
              iterator.accept = true;
            }
          }
        }

        enrichmentParameters.consents = agreements;
      }

      if (
        this.errorMsgEmail === '' &&
        this.errorMsgPhone === '' &&
        this.errorMsgDocument === '' &&
        this.errorMsgBirth === ''
      ) {
        this.resolve(enrichmentParameters);
        this.closeModal();
        this.clearFields();
      }
    },

    validateDocument() {
      return this.document.length === 11 && validateCPF(this.document) && this.parameters.showDocumentEnrichment;
    },

    validateEntityDocument() {
      if (!this.parameters.showEntityDocumentEnrichment) return false;
      if (this.document.length !== 14) return false;
      return isCNPJ(this.document);
    },
  },
};
</script>
<style>
.btn-round {
  border-radius: 30px;
}
.is-valid {
  padding-right: calc(1.5em + 0.75rem);
  background-image: url('/images/valid.svg');
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
</style>
